import { MovementCorrection } from "./presentation";
import {
  altitude,
  createWorld,
  step,
  WIDTH,
  HEIGHT,
  type World,
} from "./physics";
const correction = new MovementCorrection();
let receivedState = false;
const canvas = document.querySelector<HTMLCanvasElement>("#board")!,
  ctx = canvas.getContext("2d")!;
const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
const start = el<HTMLButtonElement>("start"),
  panel = el("panel"),
  sound = el<HTMLButtonElement>("sound");
type Peer = {
  slot: number;
  connected: boolean;
  ready: boolean;
  kind: "human" | "companion";
  world: World;
};
let world = createWorld(),
  opponent: Peer | undefined,
  socket: WebSocket | undefined;
let room = new URL(location.href).searchParams.get("room") || "",
  slot = 0,
  credential = "";
const quick = new URL(location.href).searchParams.get("quick") === "1" || !room;
const embedded = new URL(location.href).searchParams.has("embedded");
let online = false,
  running = false,
  ready = false,
  loaded = false,
  intentional = false,
  joining = false,
  fatal = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined,
  retries = 0,
  previous = 0,
  accumulator = 0,
  nudge = 0,
  lastDirection = 0;
let muted = true,
  audio: AudioContext | undefined,
  rivalX = 0,
  rivalY = 0,
  rivalInitialized = false;
let idToken = "",
  authRequired = false,
  appOrigins: string[] = [];
let joinGeneration = 0,
  joinAbort: AbortController | undefined,
  autoJoinCancelled = false;
function beginJoin() {
  joinAbort?.abort();
  joinAbort = new AbortController();
  return { generation: ++joinGeneration, signal: joinAbort.signal };
}
declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}
function bridge(type: string, data?: unknown) {
  const message = JSON.stringify({ type, data });
  if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(message);
  else if (window.parent !== window) {
    const origin = document.referrer ? new URL(document.referrer).origin : "";
    if (appOrigins.includes(origin)) window.parent.postMessage(message, origin);
  }
}
function receiveAuth(event: MessageEvent) {
  if (
    !window.ReactNativeWebView &&
    (event.source !== window.parent || !appOrigins.includes(event.origin))
  )
    return;
  try {
    const data = JSON.parse(event.data);
    if (
      data.type === "MINGLE_AUTH" &&
      typeof data.token === "string" &&
      data.token.length < 8192
    )
      idToken = data.token;
  } catch {
    /* ignore unrelated bridge messages */
  }
}
window.addEventListener("message", receiveAuth);
document.addEventListener("message", receiveAuth as EventListener);
async function apiRequest(path: string, signal: AbortSignal) {
  signal.throwIfAborted();
  if (authRequired && !idToken) {
    bridge("SPROUT_AUTH_REQUIRED");
    for (let i = 0; i < 40 && !idToken; i++) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      signal.throwIfAborted();
    }
    if (!idToken) throw Error("Open Sprout Hop from Mingle to sign in.");
  }
  const requestAbort = new AbortController();
  const abort = () => requestAbort.abort();
  signal.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(abort, 10000);
  try {
    const response = await fetch(path, {
      method: "POST",
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
      signal: requestAbort.signal,
    });
    signal.throwIfAborted();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        idToken = "";
        bridge("SPROUT_AUTH_REQUIRED");
      }
      throw Error(
        response.status === 403
          ? "A verified Mingle account is required."
          : await response.text(),
      );
    }
    return await response.json();
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
  }
}
const keys = new Set<string>(),
  pointers = new Map<number, number>();
const assets = Object.fromEntries(
  ["background", "sprout", "platform", "spring"].map((name) => {
    const image = new Image();
    image.src = `assets/${name}.png`;
    return [name, image];
  }),
);
function text(id: string, value: string) {
  if (el(id).textContent !== value) el(id).textContent = value;
}
function send(data: unknown) {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(data));
}
function input() {
  return Math.sign(
    (keys.has("ArrowRight") || keys.has("d") ? 1 : 0) -
      (keys.has("ArrowLeft") || keys.has("a") ? 1 : 0) +
      [...pointers.values()].reduce((a, b) => a + b, 0) +
      Math.sign(nudge),
  );
}
function sendInput() {
  lastDirection = online && running ? input() : 0;
  send({ type: "input", direction: lastDirection });
}
function clearInput() {
  keys.clear();
  pointers.clear();
  nudge = 0;
  sendInput();
}
function lobby() {
  panel.hidden = running && online;
  start.disabled =
    !loaded || joining || (!!room && !fatal && (!online || ready));
  el("invite-label").hidden = quick || !room;
  el("copy").hidden = quick || !room;
  el<HTMLInputElement>("invite").value = room
    ? `${location.origin}/?room=${room}`
    : "";
  if (!room) {
    text(
      "title",
      joining ? "Finding your table…" : "A little friendly rivalry.",
    );
    text("description", "Same course. Two sprouts. Keep climbing.");
    start.textContent = loaded ? "Find me a table" : "Growing your garden…";
  } else if (fatal) start.textContent = "Find another table";
  else if (!online) {
    text("title", "Connecting your race…");
    text(
      "description",
      "Rejoining automatically. Your safe platform is saved.",
    );
    start.textContent = "Connecting…";
  } else {
    text("title", opponent ? "Two sprouts. One way up." : "Room for one more.");
    text(
      "description",
      opponent
        ? opponent.kind === "companion"
          ? "Game companion · Ready when you are."
          : "You both start when you’re ready."
        : quick
          ? "Finding another player. A game companion can join after 30 seconds."
          : "Share the invite with your opponent.",
    );
    start.textContent = ready ? "Ready · waiting for player" : "Ready to hop";
  }
  text(
    "connection",
    online ? "Connected" : room ? "Reconnecting…" : "Ready when you are",
  );
}
async function connect() {
  const { generation, signal } = beginJoin();
  const connectingRoom = room;
  clearTimeout(retryTimer);
  intentional = false;
  joining = true;
  online = false;
  lobby();
  let admission = "";
  try {
    admission = (
      await apiRequest(`/api/rooms/${connectingRoom}/ticket`, signal)
    ).ticket;
    if (generation !== joinGeneration) return;
  } catch (error) {
    if (generation !== joinGeneration) return;
    joining = false;
    fatal = true;
    text("title", "Couldn’t enter this race.");
    text("description", error instanceof Error ? error.message : "Try again.");
    lobby();
    return;
  }
  const ws = new WebSocket(
    `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/race`,
  );
  socket = ws;
  ws.addEventListener("open", () => {
    if (generation !== joinGeneration || ws !== socket) {
      ws.close();
      return;
    }
    try {
      credential = sessionStorage.getItem(`sprout:${room}`) || credential;
    } catch {
      /* in-memory fallback */
    }
    send({ type: "join", room, token: credential, ticket: admission });
  });
  ws.addEventListener("message", (event) => {
    if (generation !== joinGeneration || ws !== socket) return;
    const data = JSON.parse(event.data);
    if (data.type === "joined") {
      receivedState = false;
      slot = data.slot;
      credential = data.token;
      online = true;
      joining = false;
      fatal = false;
      retries = 0;
      try {
        sessionStorage.setItem(`sprout:${room}`, credential);
      } catch {
        /* private mode */
      }
      lobby();
    }
    if (data.type === "state") {
      if (Number.isInteger(data.selfSlot) && data.selfSlot >= 0)
        slot = data.selfSlot;
      const self = data.players.find((p: Peer) => p.slot === slot);
      if (!self) return;
      const wasRespawning = world.respawn > 0;
      correction.reconcile(
        world,
        self.world,
        !receivedState || !running || !data.running,
      );
      world = self.world;
      receivedState = true;
      ready = self.ready;
      running = data.running;
      opponent = data.players.find((p: Peer) => p.slot !== slot);
      if (!opponent) rivalInitialized = false;
      if (wasRespawning !== world.respawn > 0) {
        clearInput();
        text(
          "announcement",
          world.respawn > 0
            ? "Returning to your last platform in five seconds."
            : "Back on your safe platform. Keep climbing.",
        );
      }
      lobby();
    }
    if (data.type === "error") {
      if (data.message === "This player is already connected.") return;
      fatal = true;
      joining = false;
      text("title", "Can’t join this race.");
      text("description", data.message);
      lobby();
    }
  });
  ws.addEventListener("close", () => {
    if (generation !== joinGeneration || ws !== socket) return;
    online = false;
    joining = false;
    clearInput();
    lobby();
    if (!intentional && !fatal)
      retryTimer = setTimeout(connect, Math.min(500 * 2 ** retries++, 5000));
  });
  ws.addEventListener("error", () => {});
}
async function findTable() {
  if (!loaded) return;
  if (!room || fatal) {
    const { generation, signal } = beginJoin();
    intentional = true;
    socket?.close();
    clearTimeout(retryTimer);
    credential = "";
    joining = true;
    fatal = false;
    start.disabled = true;
    try {
      const data = await apiRequest(
        quick ? "/api/matchmake" : "/api/rooms",
        signal,
      );
      if (generation !== joinGeneration) return;
      room = data.id;
      history.replaceState(
        null,
        "",
        `?${embedded ? "embedded=1&" : ""}${quick ? "quick=1&" : ""}room=${room}`,
      );
      opponent = undefined;
      world = createWorld();
      running = false;
      ready = false;
      connect();
    } catch (error) {
      if (generation !== joinGeneration) return;
      joining = false;
      room = "";
      lobby();
      text(
        "description",
        error instanceof Error ? error.message : "Couldn’t create a race.",
      );
    }
  } else send({ type: "ready" });
}
start.addEventListener("click", () => {
  void findTable();
});
el("copy").addEventListener("click", async () => {
  if (window.ReactNativeWebView) {
    bridge("SPROUT_INVITE", { room });
    return;
  }
  try {
    await navigator.clipboard.writeText(el<HTMLInputElement>("invite").value);
    text("copy", "Copied");
  } catch {
    el<HTMLInputElement>("invite").select();
    text("copy", "Select and copy link");
  }
});
el("back").addEventListener("click", () => {
  autoJoinCancelled = true;
  ++joinGeneration;
  joinAbort?.abort();
  intentional = true;
  clearTimeout(retryTimer);
  clearInput();
  send({ type: "leave" });
  socket?.close();
  try {
    sessionStorage.removeItem(`sprout:${room}`);
  } catch {
    /* private mode */
  }
  credential = "";
  socket = undefined;
  if (embedded) {
    bridge("SPROUT_EXIT");
    return;
  }
  socket = undefined;
  room = "";
  credential = "";
  opponent = undefined;
  online = false;
  running = false;
  ready = false;
  fatal = false;
  joining = false;
  world = createWorld();
  history.replaceState(null, "", "/");
  lobby();
});
function tone(boost: boolean) {
  if (muted || !audio) return;
  const osc = audio.createOscillator(),
    gain = audio.createGain();
  osc.connect(gain);
  gain.connect(audio.destination);
  osc.frequency.setValueAtTime(boost ? 540 : 340, audio.currentTime);
  osc.frequency.exponentialRampToValueAtTime(
    boost ? 900 : 500,
    audio.currentTime + 0.08,
  );
  gain.gain.setValueAtTime(0.025, audio.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.12);
  osc.start();
  osc.stop(audio.currentTime + 0.13);
}
sound.addEventListener("click", () => {
  muted = !muted;
  if (!muted) {
    audio ??= new AudioContext();
    void audio.resume();
    tone(false);
  }
  sound.textContent = String.fromCodePoint(muted ? 61969 : 61968);
  sound.setAttribute("aria-label", muted ? "Enable sound" : "Mute sound");
  sound.setAttribute("aria-pressed", String(!muted));
});
for (const [id, direction] of [
  ["left", -1],
  ["right", 1],
] as const) {
  const button = el<HTMLButtonElement>(id);
  button.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    if (!running || !online) return;
    pointers.set(e.pointerId, direction);
    button.setPointerCapture(e.pointerId);
    sendInput();
  });
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"])
    button.addEventListener(type, (e) => {
      pointers.delete((e as PointerEvent).pointerId);
      sendInput();
    });
  button.addEventListener("click", (e) => {
    if (e.detail === 0) {
      nudge = direction * 0.15;
      sendInput();
    }
  });
}
canvas.addEventListener("pointerdown", (e) => {
  if (!running || !online) return;
  const rect = canvas.getBoundingClientRect();
  pointers.set(e.pointerId, e.clientX - rect.left < rect.width / 2 ? -1 : 1);
  canvas.setPointerCapture(e.pointerId);
  sendInput();
});
for (const type of ["pointerup", "pointercancel", "lostpointercapture"])
  canvas.addEventListener(type, (e) => {
    pointers.delete((e as PointerEvent).pointerId);
    sendInput();
  });
document.addEventListener("keydown", (e) => {
  if (["ArrowLeft", "ArrowRight", "a", "d"].includes(e.key)) {
    e.preventDefault();
    keys.add(e.key);
    sendInput();
  }
});
document.addEventListener("keyup", (e) => {
  keys.delete(e.key);
  sendInput();
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden) clearInput();
});
window.addEventListener("blur", clearInput);
setInterval(sendInput, 100);
function sprout(x: number, y: number, rival: boolean, label: string) {
  ctx.save();
  if (rival) ctx.filter = "hue-rotate(235deg) saturate(.65)";
  ctx.drawImage(assets.sprout, 207, 97, 865, 1060, x - 24, y - 60, 48, 60);
  ctx.restore();
  ctx.font = "600 11px Inter";
  ctx.textAlign = "center";
  ctx.fillStyle = rival ? "#775b93" : "#a34326";
  ctx.fillText(label, x, y - 66);
}
function draw(dt: number) {
  const position = correction.position(world);
  const d = Math.min(window.devicePixelRatio || 1, 2);
  if (canvas.width !== WIDTH * d) {
    canvas.width = WIDTH * d;
    canvas.height = HEIGHT * d;
  }
  ctx.setTransform(d, 0, 0, d, 0, 0);
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  if (!loaded) return;
  ctx.drawImage(assets.background, 0, 0, WIDTH, HEIGHT);
  for (const p of world.platforms) {
    const y = p.y - position.camera;
    if (y < 110 || y > HEIGHT + 30) continue;
    ctx.save();
    if (p.kind === "moving") ctx.filter = "hue-rotate(190deg) saturate(.65)";
    if (p.kind === "spring")
      ctx.drawImage(assets.spring, 285, 270, 1204, 406, p.x, y, p.width, 36);
    else
      ctx.drawImage(assets.platform, 210, 244, 1726, 250, p.x, y, p.width, 24);
    ctx.restore();
    if (p.kind === "moving") {
      ctx.font = "14px Feather";
      ctx.textAlign = "center";
      ctx.fillStyle = "#fff9f1";
      ctx.fillText(
        String.fromCodePoint(61743) + String.fromCodePoint(61744),
        p.x + p.width / 2,
        y + 15,
      );
    }
  }
  if (opponent) {
    const other = opponent.world;
    const opponentName =
      opponent.kind === "companion"
        ? "Game companion"
        : `Player ${opponent.slot + 1}`;
    if (!rivalInitialized) {
      rivalX = other.x;
      rivalY = other.y;
      rivalInitialized = true;
    }
    const blend = 1 - Math.exp(-18 * dt);
    rivalX += (other.x - rivalX) * blend;
    rivalY += (other.y - rivalY) * blend;
    const sy = rivalY - position.camera;
    if (other.respawn === 0 && sy > 155 && sy < HEIGHT - 100)
      sprout(
        rivalX,
        sy,
        true,
        opponent.kind === "companion"
          ? "Companion"
          : `Player ${opponent.slot + 1}`,
      );
    else if (running) {
      ctx.font = "600 12px Inter";
      ctx.fillStyle = "#775b93";
      ctx.textAlign = "right";
      ctx.fillText(
        other.respawn > 0
          ? `${opponentName} · back in ${Math.ceil(other.respawn)}s`
          : `${opponentName} ${sy < 155 ? "↑" : "↓"} ${Math.max(0, Math.floor((742 - other.y) / 8))}m`,
        WIDTH - 16,
        sy < 155 ? 146 : HEIGHT - 145,
      );
    }
  }
  if (world.respawn === 0)
    sprout(position.x, position.y - position.camera, false, "You");
  el("respawn").hidden = world.respawn <= 0 || !running;
  text("respawn", `Back in ${Math.ceil(world.respawn)}s · last safe platform`);
  text("altitude", `You · ${altitude(world)} m`);
  text(
    "opponent",
    opponent
      ? `${opponent.kind === "companion" ? "Game companion" : `Player ${opponent.slot + 1}`} · ${altitude(opponent.world)} m${opponent.connected ? "" : " · reconnecting"}`
      : "Waiting for player 2",
  );
  text(
    "status",
    running
      ? "Live · 2-player race"
      : online
        ? `${opponent ? "2" : "1"}/2 players`
        : "2-player race",
  );
}
function frame(time: number) {
  const dt = Math.min((time - previous) / 1000, 0.05);
  accumulator += dt;
  previous = time;
  if (running && online && loaded) {
    while (accumulator >= 1 / 60) {
      const event = step(world, input(), 1 / 60);
      if (event === "hop" || event === "spring") tone(event === "spring");
      nudge = Math.sign(nudge) * Math.max(0, Math.abs(nudge) - 1 / 60);
      accumulator -= 1 / 60;
    }
  } else accumulator = 0;
  if (lastDirection !== input()) sendInput();
  correction.advance(dt);
  draw(dt);
  requestAnimationFrame(frame);
}
Promise.all([
  ...Object.values(assets).map((i) => i.decode()),
  fetch("/config")
    .then((r) => r.json())
    .then((config) => {
      authRequired = config.authRequired;
      appOrigins = config.appOrigins;
      if (!authRequired) {
        try {
          idToken =
            sessionStorage.getItem("sprout:guest") || crypto.randomUUID();
          sessionStorage.setItem("sprout:guest", idToken);
        } catch {
          idToken = crypto.randomUUID();
        }
      }
    }),
])
  .then(() => {
    loaded = true;
    bridge("SPROUT_READY");
    lobby();
    if (autoJoinCancelled) return;
    if (room) connect();
    else if (quick) void findTable();
  })
  .catch(() => {
    text("description", "The garden couldn’t load. Refresh to try again.");
  });
lobby();
requestAnimationFrame(frame);
